import { DIMMED_POINT_OPACITY, MUTED_POINT_OPACITY } from 'enums/charts.enums';
import { IChartDot } from 'interfaces/session-recap/i-chart-dot';
import { IPointer } from 'interfaces/session-recap/i-pointer';
import {
  MouseEvent,
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

interface IProps {
  dots: IChartDot[];
  hoveredID: string | null;
  selectedID: string | null;
  onEnter: (id: string, pointer: IPointer) => void;
  /** id of the dot the pointer left; a late leave must not clear a newer hover */
  onLeave: (id: string) => void;
  onSelect: (id: string) => void;
  /** while a legend item is hovered, other categories fade */
  highlightKey?: string | null;
}

const toPointer = (e: MouseEvent): IPointer => ({ x: e.clientX, y: e.clientY });

/** PlateDot / BreakDot: 4px mark, 8px hit target, 75% fill, 1px same-colour stroke */
const DOT_RADIUS_PX = 4;
const HOVER_RADIUS_PX = 8;
const DOT_FILL_OPACITY = 0.75;

/** screen pixels of the scaled svg, so a 4px dot stays 4px inside the viewBox */
const useUnitsPerPx = (ref: RefObject<SVGGElement | null>) => {
  const [unitsPerPx, setUnitsPerPx] = useState(1);

  useLayoutEffect(() => {
    const svg = ref.current?.ownerSVGElement;

    if (!svg) {
      return;
    }

    const measure = () => {
      const ctm = svg.getScreenCTM();
      const pxPerUnit = ctm ? Math.hypot(ctm.a, ctm.b) : 0;

      if (pxPerUnit <= 0) {
        return;
      }

      const next = 1 / pxPerUnit;
      setUnitsPerPx((current) =>
        Math.abs(current - next) < 0.01 ? current : next
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(svg);
    return () => observer.disconnect();
  }, [ref]);

  return unitsPerPx;
};

/**
 * Same hit target as the admin plate plot: a transparent circle twice the
 * visual dot, so the mark never grows over the next pitch.
 */
export const ChartDots = (props: IProps) => {
  const { dots, hoveredID, selectedID, highlightKey } = props;

  const groupRef = useRef<SVGGElement>(null);
  const unitsPerPx = useUnitsPerPx(groupRef);
  const radius = DOT_RADIUS_PX * unitsPerPx;
  const hitRadius = HOVER_RADIUS_PX * unitsPerPx;

  const isLegendHighlighting = !!highlightKey;

  /**
   * Hover dims every other pitch. A selected pitch keeps that dim after the
   * pointer leaves, so the open pitch card does not restore the cloud.
   * A legend highlight stays in charge while the pointer is on a legend item.
   */
  const getOpacityForDot = useCallback(
    (dot: IChartDot) => {
      const isHovered = dot.id === hoveredID;
      const isSelected = dot.id === selectedID;

      if (isLegendHighlighting && dot.legendKey !== highlightKey) {
        // highlighting a legend item dims all dots for other categories
        return DIMMED_POINT_OPACITY;
      }

      if (isHovered) {
        // hovered dot is fully opaque
        return 1;
      }

      if (hoveredID) {
        if (isSelected) {
          // while hovering, a selected dot is fully opaque (even if it's not the hovered dot)
          return 1;
        }

        // hovered dot dims other dots if not selected
        return DIMMED_POINT_OPACITY;
      }

      if (isSelected) {
        // selected dot is fully opaque
        return 1;
      }

      if (!isLegendHighlighting && selectedID) {
        // legend is not hovered, non-selected dots are dimmed
        return DIMMED_POINT_OPACITY;
      }

      if (dot.muted) {
        // muted dots are dimmed
        return MUTED_POINT_OPACITY;
      }

      // default to fully opaque
      return 1;
    },
    [hoveredID, selectedID, isLegendHighlighting, highlightKey]
  );

  return (
    <g
      ref={groupRef}
      className="recap-dots"
      data-hovering={!!hoveredID || !!selectedID || isLegendHighlighting}
    >
      {dots.map((d) => {
        const hovered = d.id === hoveredID;
        const selected = d.id === selectedID;

        return (
          <g
            key={d.id}
            opacity={getOpacityForDot(d)}
            onMouseEnter={(e) => props.onEnter(d.id, toPointer(e))}
            onMouseLeave={() => props.onLeave(d.id)}
            onClick={() => props.onSelect(d.id)}
          >
            <circle
              className="recap-dot-hit"
              cx={d.x}
              cy={d.y}
              r={hitRadius}
              fill="transparent"
              pointerEvents="all"
            />
            {(hovered || selected) && (
              <circle
                className="recap-dot-outline"
                cx={d.x}
                cy={d.y}
                r={radius}
                pointerEvents="none"
              />
            )}
            <circle
              className="recap-dot-mark"
              data-hovered={hovered}
              data-selected={selected}
              cx={d.x}
              cy={d.y}
              r={radius}
              fill={d.color}
              fillOpacity={DOT_FILL_OPACITY}
              stroke={d.color}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          </g>
        );
      })}
    </g>
  );
};
